import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { isEmpty } from 'lodash';

import { operations as generalReportOperations } from '../../../state/modules/general-reports';
import { selectors as generalReportSelectors } from '../../../state/modules/general-reports';
import TableReport from './TableReport';
import Loader from '../../general/Loader';
import { unpackFromLocalStorage, addToLocalStorage } from '../../../utils/local-storage';
import { ProgressBar } from '../../ui-kit';

import FilterModal from './FilterModal';

export const GeneralReport = ({
  fetchArchetype,
  fetchData,
  reportType,
  filters,
  reportData,
  defaultColumnKeys,
  columnsByKey,
  progress,
  downloadInProgress
}) => {
  const [selectedColumns, setSelectedColumns] = useState(
    unpackFromLocalStorage(`general_report_${reportType}_columns`) || null
  );
  const [userFiltering, setUserFiltering] = useState([]);
  const [userSorting, setUserSorting] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  if (!filters) filters = {};
  // remove all nulls from the filters
  filters = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v != null && v !== ''));

  const fetchReportData = (fileType, extraParams = {}) =>
    fetchData({
      params: { ...filters, cols: (selectedColumns || []).join(','), file_type: fileType, ...extraParams },
      reportType
    });

  useEffect(() => {
    fetchArchetype(reportType, filters);
    fetchReportData();
  }, []);

  useEffect(() => {
    if (selectedColumns !== null || isEmpty(defaultColumnKeys)) return;
    setSelectedColumns(defaultColumnKeys);
  }, [selectedColumns, defaultColumnKeys]);

  useEffect(() => {
    globalThis.instantiatePopovers();
    globalThis.ReactRailsUJS?.mountComponents();
  }, [reportData]);

  const setSelectedColumnsAndSave = newSelectedColumns => {
    setSelectedColumns(newSelectedColumns);
    addToLocalStorage(`general_report_${reportType}_columns`, newSelectedColumns);
  };

  const extraParams = useMemo(
    () => ({
      user_filtering: JSON.stringify(userFiltering),
      user_sorting: JSON.stringify(userSorting)
    }),
    [userFiltering, userSorting]
  );

  const exportToCSV = () => fetchReportData('csv', extraParams);
  const exportToXLSX = () => fetchReportData('xlsx', extraParams);
  const handleRefresh = () => fetchReportData(undefined, extraParams);

  const columnKeys = isEmpty(reportData) ? [] : Object.keys(reportData[0]);

  if (!selectedColumns) return <Loader />;

  return (
    <>
      {progress > 0 && progress < 100 && <ProgressBar now={progress} className="mb-2" style={{ height: '0.3rem' }} />}
      <TableReport
        columnsByKey={columnsByKey}
        data={reportData}
        columnKeys={columnKeys}
        handleShowFilters={() => setShowFilters(true)}
        handleRefresh={handleRefresh}
        exportToCSV={exportToCSV}
        exportToXLSX={exportToXLSX}
        downloadInProgress={downloadInProgress}
        onFilteredChange={setUserFiltering}
        onSortedChange={setUserSorting}
      />
      <FilterModal
        selectedColumns={selectedColumns}
        columnsByKey={columnsByKey}
        fetchData={fetchReportData}
        show={showFilters}
        setShow={setShowFilters}
        setSelectedColumns={setSelectedColumnsAndSave}
        defaultColumnKeys={defaultColumnKeys}
      />
    </>
  );
};

const mapStateToProps = state => ({
  reportData: generalReportSelectors.data(state),
  columnsByKey: generalReportSelectors.columnsByKey(state),
  defaultColumnKeys: generalReportSelectors.defaultColumnKeys(state),
  progress: generalReportSelectors.progress(state),
  downloadInProgress: generalReportSelectors.downloadInProgress(state)
});

const mapDispatchToProps = {
  fetchArchetype: generalReportOperations.fetchArchetype,
  fetchData: generalReportOperations.fetchData
};

export default connect(mapStateToProps, mapDispatchToProps)(GeneralReport);
